import { useEffect, useMemo, useRef, useState } from 'react';
import { ApiError, api, apiErrorCode } from '../api';
import type { TranscriptionJob, TranscriptionOverview } from '../types';
import { formatDate, formatDuration } from '../utils';
import { Icon } from './Icon';
import { QuotaHelp } from './QuotaHelp';

const emptyOverview: TranscriptionOverview = {
  jobs: [],
  availability: { standard: false, diarization: false },
  quotaManaged: false,
  quota: null,
};

function formatAllowance(seconds: number): string {
  const totalMinutes = Math.ceil(Math.max(0, seconds) / 60);
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  if (!hours) return `${minutes} min`;
  return minutes ? `${hours} hr ${minutes} min` : `${hours} hr`;
}

export function TranscriptionPanel({ videoId, durationSeconds, onCompleted }: {
  videoId: string;
  durationSeconds: number;
  onCompleted: () => void;
}) {
  const [overview, setOverview] = useState(emptyOverview);
  const [loading, setLoading] = useState(true);
  const [overviewLoaded, setOverviewLoaded] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [quotaBlocked, setQuotaBlocked] = useState(false);
  const [languageCode, setLanguageCode] = useState('id');
  const [label, setLabel] = useState('Bahasa Indonesia');
  const [identifySpeakers, setIdentifySpeakers] = useState(false);
  const [isDefault, setIsDefault] = useState(true);
  const [replaceExisting, setReplaceExisting] = useState(false);
  const reported = useRef(new Set<string>());
  const active = overview.jobs.some((job) => job.status === 'queued' || job.status === 'running');
  const estimate = useMemo(() => Math.max(0, durationSeconds) / 60 * 0.006, [durationSeconds]);
  const estimatedQuotaSeconds = Math.max(0, Math.ceil(durationSeconds));
  const includedRemaining = overview.quota
    ? Math.max(0, overview.quota.allowanceSeconds - overview.quota.consumedSeconds - overview.quota.reservedSeconds)
    : null;
  const creditRemaining = overview.quota?.creditAvailableSeconds ?? 0;
  const quotaRemaining = includedRemaining === null ? null : includedRemaining + creditRemaining;
  const quotaInsufficient = !active && quotaRemaining !== null && estimatedQuotaSeconds > quotaRemaining;

  async function load(silent = false) {
    if (!silent) setLoading(true);
    try {
      const next = await api.transcriptions(videoId);
      setOverview(next);
      setOverviewLoaded(true);
      setQuotaBlocked(false);
      for (const job of next.jobs) {
        if (job.status === 'succeeded' && !reported.current.has(job.id)) {
          reported.current.add(job.id);
          onCompleted();
        }
      }
    } catch (reason) {
      if (!silent) setError(reason instanceof ApiError ? reason.message : 'Unable to load transcription jobs.');
    } finally {
      if (!silent) setLoading(false);
    }
  }

  useEffect(() => {
    reported.current.clear();
    setOverview(emptyOverview);
    setOverviewLoaded(false);
    setError('');
    setQuotaBlocked(false);
    void load();
  }, [videoId]);
  useEffect(() => {
    if (!active) return;
    const timer = window.setInterval(() => void load(true), 5_000);
    return () => window.clearInterval(timer);
  }, [active, videoId]);

  async function generate() {
    setBusy(true);
    setError('');
    setQuotaBlocked(false);
    try {
      await api.requestTranscription(videoId, {
        languageCode: languageCode.trim() || null,
        label: label.trim() || 'Generated subtitles',
        identifySpeakers,
        isDefault,
        replaceExisting,
      });
      await load();
    } catch (reason) {
      setError(reason instanceof ApiError ? reason.message : 'Unable to queue transcription.');
      setQuotaBlocked(apiErrorCode(reason) === 'subtitle_credit_exhausted');
    } finally {
      setBusy(false);
    }
  }

  async function cancel(job: TranscriptionJob) {
    setBusy(true);
    setError('');
    try {
      await api.cancelTranscription(videoId, job.id);
      await load();
    } catch (reason) {
      setError(reason instanceof ApiError ? reason.message : 'Unable to cancel transcription.');
    } finally {
      setBusy(false);
    }
  }

  const available = identifySpeakers ? overview.availability.diarization : overview.availability.standard;
  return <section className="transcription-panel">
    <div className="video-details__section-heading"><div><span>{loading || !overviewLoaded ? 'Subtitles' : overview.quotaManaged ? 'AI subtitles' : 'Local agent + OpenAI'}</span><h3>Generate subtitles</h3></div></div>
    {!loading && overviewLoaded && <p className="video-details__explanation">{overview.quotaManaged ? 'Generate subtitles from this video. Usage counts toward your AI subtitle allowance.' : "Subtitle generation runs separately from video processing. After you select Generate subtitles, the local Docker agent extracts temporary audio, sends only that audio to OpenAI, and returns WebVTT. The audio is deleted after the job; the API key stays in the agent's private volume."}</p>}
    {error && <p className="form-alert" role="alert"><Icon name="warning" size={16} />{error}</p>}
    <div className="transcription-panel__form">
      <label><span>Language</span><select className="control-select" aria-label="Transcription language" value={languageCode} onChange={(event) => {
        setLanguageCode(event.target.value);
        if (event.target.value === '') setLabel('Auto-detected subtitles');
        if (event.target.value === 'id') setLabel('Bahasa Indonesia');
        if (event.target.value === 'en') setLabel('English');
      }}><option value="">Auto-detect</option><option value="id">Bahasa Indonesia</option><option value="en">English</option></select></label>
      <label><span>Caption label</span><input aria-label="Generated caption label" value={label} maxLength={80} onChange={(event) => setLabel(event.target.value)} /></label>
      <label className="checkbox-field transcription-panel__wide"><input className="control-choice" type="checkbox" checked={identifySpeakers} onChange={(event) => setIdentifySpeakers(event.target.checked)} /><span>Identify speakers<small>Useful for webinar Q&amp;A. Captions include Speaker A, Speaker B, and so on.</small></span></label>
      <label className="checkbox-field transcription-panel__wide"><input className="control-choice" type="checkbox" checked={isDefault} onChange={(event) => setIsDefault(event.target.checked)} /><span>Enable by default<small>Viewers can still turn subtitles off in the player.</small></span></label>
      <label className="checkbox-field transcription-panel__wide"><input className="control-choice" type="checkbox" checked={replaceExisting} onChange={(event) => setReplaceExisting(event.target.checked)} /><span>Replace same-language subtitles<small>The existing track is replaced only after generation succeeds.</small></span></label>
    </div>
    {!loading && overviewLoaded && (overview.quotaManaged ? overview.quota ? <div className={`transcription-panel__estimate ${quotaInsufficient ? 'is-warning' : active ? 'is-reserved' : 'is-available'}`} role={quotaInsufficient ? 'alert' : undefined}>
      <strong>{quotaInsufficient ? 'Not enough AI subtitle quota' : active ? 'AI subtitle quota reserved' : 'Estimated AI subtitle usage'}</strong>
      <span>{formatAllowance(estimatedQuotaSeconds)} for this video · {formatAllowance(quotaRemaining ?? 0)} available after current reservations.</span>
      {creditRemaining > 0 && <span>Includes {formatAllowance(creditRemaining)} paid credit{overview.quota?.creditEarliestExpiry ? `; earliest expiry ${formatDate(overview.quota.creditEarliestExpiry)}` : ''}.</span>}
      <span>{quotaInsufficient
        ? `This request needs ${formatAllowance(estimatedQuotaSeconds - (quotaRemaining ?? 0))} more than the remaining AI subtitle allowance and cannot be queued.`
        : active
          ? 'This queued or running job has already reserved its allowance.'
          : 'The estimate fits within the remaining AI subtitle allowance. Final usage follows the video duration.'}</span>
    </div> : <div className="transcription-panel__estimate is-reserved">
      <strong>AI subtitle allowance</strong>
      <span>{formatAllowance(estimatedQuotaSeconds)} estimated for this video.</span>
      <span>Allowance details are not available for this library.</span>
    </div> : <div className="transcription-panel__estimate">
      {identifySpeakers
        ? <><strong>Usage-based price</strong><span>Diarization is token-priced. Review final usage after completion.</span><a href="https://developers.openai.com/api/docs/models/gpt-4o-transcribe-diarize" target="_blank" rel="noreferrer">Current OpenAI pricing</a></>
        : <><strong>Estimated ${estimate.toFixed(3)}</strong><span>{formatDuration(durationSeconds)} × $0.006/minute. Whisper provides the timestamps required for synchronized WebVTT.</span><a href="https://developers.openai.com/api/docs/models/whisper-1" target="_blank" rel="noreferrer">Current OpenAI pricing</a></>}
    </div>)}
    {overview.quotaManaged && (quotaInsufficient || quotaBlocked) && <QuotaHelp resource="subtitle" onRefresh={() => load()} />}
    {!loading && overviewLoaded && !available && <p className="transcription-panel__unavailable"><Icon name="server" size={17} />{overview.quotaManaged ? 'Subtitle generation is temporarily unavailable. Please try again later.' : <>No online agent currently advertises {identifySpeakers ? 'OpenAI diarization' : 'an OpenAI API key'}. Configure the key locally, then restart or wait for its next heartbeat.</>}</p>}
    <button type="button" className="button button--primary" disabled={busy || loading || active || quotaInsufficient || !available || !label.trim()} onClick={() => void generate()}>
      {busy ? <span className="spinner" /> : <Icon name="plus" size={16} />}Generate subtitles
    </button>
    {!!overview.jobs.length && <div className="transcription-panel__jobs">
      {overview.jobs.slice(0, 5).map((job) => <article key={job.id}>
        <div><strong>{job.identifySpeakers ? 'Speaker-aware transcript' : 'Standard subtitles'}</strong><span>{job.stage.replaceAll('_', ' ')} · {Math.round(job.progress * 100)}%</span><small>{formatDate(job.createdAt)}{!overview.quotaManaged && job.costUsd !== null ? ` · $${job.costUsd.toFixed(4)}` : ''}</small>{job.errorMessage && <small className="danger">{job.errorMessage}</small>}</div>
        <span className={`status-badge status-badge--${job.status === 'running' ? 'processing' : job.status}`}>{job.status}</span>
        {(job.status === 'queued' || job.status === 'running') && <button type="button" className="button button--secondary" disabled={busy} onClick={() => void cancel(job)}>Cancel</button>}
      </article>)}
    </div>}
  </section>;
}
